import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ReadingProgress from '@/components/feature/ReadingProgress';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, getArticleById, getRelatedArticles, hotRank } from '@/mocks/articles';
import { formatHeat } from '@/utils/format';
import useSeo from '@/hooks/useSeo';

const ArticleDetail = () => {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const index = articles.findIndex((item) => item.id === id);
  const prev = index > 0 ? articles[index - 1] : null;
  const next = index >= 0 && index < articles.length - 1 ? articles[index + 1] : null;

  useSeo({
    title: article ? article.title : '爆料详情 - 看瓜爆料',
    description: article ? article.excerpt : '看瓜爆料独家一手私料详情页。',
    keywords: article
      ? `看瓜爆料,${article.topicName},${article.tags.join(',')},独家爆料`
      : '看瓜爆料,爆料详情',
    path: `/article/${id}`,
    ogType: 'article',
    schema: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            description: article.excerpt,
            image: [article.cover],
            datePublished: article.date,
            dateModified: article.date,
            articleSection: article.topicName,
            keywords: article.tags.join(','),
            inLanguage: 'zh-CN',
            author: { '@type': 'Organization', name: article.author },
            publisher: { '@type': 'Organization', name: '看瓜爆料' },
            mainEntityOfPage: `/article/${article.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: '爆料库', item: '/topics' },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <SiteLayout>
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '爆料库', to: '/topics' }, { label: '内容不存在' }]} />
        <section className="mx-auto flex max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
            <i className="ri-file-unknow-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条爆料不存在或已下线</h1>
          <p className="mt-3 text-sm text-foreground-600">可能是链接有误，去爆料库看看最新内容吧。</p>
          <Link
            to="/topics"
            className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回爆料库
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const related = getRelatedArticles(article, 3);
  const rankList = hotRank
    .map((item) => articles.find((a) => a.id === item.id))
    .filter((item): item is (typeof articles)[number] => Boolean(item))
    .slice(0, 5);

  return (
    <SiteLayout>
      <ReadingProgress />
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '爆料库', to: '/topics' },
          { label: article.topicName, to: `/topic/${article.topicSlug}` },
          { label: article.title },
        ]}
      />

      <div className="mx-auto grid max-w-content grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-[1fr_320px]">
        <article>
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/topic/${article.topicSlug}`}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {article.topicName}
              </Link>
              {article.isHot && (
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  独家首发
                </span>
              )}
              <span className="rounded-full border border-background-200/70 px-3 py-1 text-xs text-foreground-600">
                内部首发
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>

            <p className="mt-4 rounded-md border-l-4 border-primary-400 bg-background-100/70 px-4 py-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              {article.excerpt}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line text-[15px]"></i>
                {article.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-[15px]"></i>
                {article.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-[15px]"></i>
                阅读约 {article.readTime}
              </span>
              <span className="inline-flex items-center gap-1.5 text-primary-600">
                <i className="ri-eye-line text-[15px]"></i>
                {formatHeat(article.heat)} 阅读
              </span>
            </div>
          </header>

          <figure className="mt-7 overflow-hidden rounded-lg border border-background-200/70">
            <div className="h-56 w-full overflow-hidden md:h-96">
              <img
                src={article.cover}
                alt={`${article.title} - 看瓜爆料独家爆料配图`}
                title={`${article.title} - 看瓜爆料`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <figcaption className="bg-background-100/70 px-4 py-2.5 text-xs text-foreground-500">
              配图与本文内容无直接对应关系，仅作氛围示意。
            </figcaption>
          </figure>

          <div className="mt-8 space-y-5">
            {article.paragraphs.map((paragraph, index2) => (
              <p key={index2} className="text-sm leading-8 text-foreground-800 md:text-base md:leading-9">
                {paragraph}
              </p>
            ))}
          </div>

          {article.quote && (
            <blockquote className="mt-8 rounded-lg bg-accent-50 px-6 py-5">
              <i className="ri-double-quotes-l text-3xl text-accent-500"></i>
              <p className="mt-1 font-heading text-base font-semibold leading-relaxed text-accent-950 md:text-lg">
                {article.quote}
              </p>
            </blockquote>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-6">
            <span className="text-xs text-foreground-500">标签</span>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/topics?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-6 rounded-md bg-secondary-50 px-4 py-3 text-xs leading-relaxed text-foreground-600">
            <i className="ri-information-line mr-1 text-secondary-700"></i>
            本文为虚构娱乐内容，文中人物均以匿名方式表述，如涉及事实出入欢迎通过联系我们页面反馈。
          </div>

          <nav className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" aria-label="上一篇下一篇">
            {prev ? (
              <Link
                to={`/article/${prev.id}`}
                className="group rounded-lg border border-background-200/70 bg-background-100/50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-arrow-left-line"></i>
                  上一篇
                </span>
                <span className="mt-1.5 line-clamp-2 block text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                  {prev.title}
                </span>
              </Link>
            ) : (
              <div className="rounded-lg border border-dashed border-background-200/70 p-4 text-xs text-foreground-400">
                已经是第一篇了
              </div>
            )}
            {next ? (
              <Link
                to={`/article/${next.id}`}
                className="group rounded-lg border border-background-200/70 bg-background-100/50 p-4 text-right transition-colors hover:border-primary-300"
              >
                <span className="flex items-center justify-end gap-1 text-xs text-foreground-500">
                  下一篇
                  <i className="ri-arrow-right-line"></i>
                </span>
                <span className="mt-1.5 line-clamp-2 block text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                  {next.title}
                </span>
              </Link>
            ) : (
              <div className="rounded-lg border border-dashed border-background-200/70 p-4 text-right text-xs text-foreground-400">
                已经是最后一篇了
              </div>
            )}
          </nav>
        </article>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-fire-fill text-lg text-primary-500"></i>
              今日热榜
            </h2>
            <ol className="mt-4 space-y-3">
              {rankList.map((item, i) => (
                <li key={item.id} className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                      i < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <Link
                    to={`/article/${item.id}`}
                    className="line-clamp-2 text-sm leading-snug text-foreground-800 hover:text-primary-600"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          {related.length > 0 && (
            <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                相关推荐
              </h2>
              <ul className="mt-4 space-y-4">
                {related.map((item) => (
                  <li key={item.id} className="flex gap-3">
                    <Link to={`/article/${item.id}`} className="h-16 w-20 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={item.cover}
                        alt={`${item.title} - 看瓜爆料`}
                        className="h-full w-full object-cover object-top"
                      />
                    </Link>
                    <div className="min-w-0">
                      <Link
                        to={`/article/${item.id}`}
                        className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 hover:text-primary-600"
                      >
                        {item.title}
                      </Link>
                      <p className="mt-1 text-[11px] text-foreground-500">{item.readTime} · {item.dateLabel}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link
            to="/#subscribe"
            className="block rounded-lg bg-primary-500 p-5 text-background-50 transition-colors hover:bg-primary-600"
          >
            <p className="flex items-center gap-2 font-heading text-base font-bold">
              <i className="ri-mail-send-line text-lg"></i>
              订阅爆料周报
            </p>
            <p className="mt-2 text-xs leading-relaxed text-primary-50">
              每周一封，收到最值得看的 3 条爆料和 1 个幕后观察。
            </p>
          </Link>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="border-t border-background-200/70 bg-background-100/40 py-12">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950">
              同栏目继续看
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
};

export default ArticleDetail;